import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiBreadcrumb, UiDataTable } from 'components/common'
import { Box, Button, Flex, Heading, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, useDisclosure } from '@chakra-ui/react'
import { ROLENAMES } from 'lib/role'
import { deleteUser, getUsers } from 'api/userApi'
import { Link as NavLink, useNavigate } from 'react-router-dom'
import { MdAdd } from 'react-icons/md'
import { useAppContext } from 'hooks/useAppContext'

const BreadcrumbNav = [
  {
    label: 'User Management',
    path: '/system/users',
    isCurrentPage: true
  }
]

const USER_COLUMNS = [
  {
    label: 'Name',
    orderBy: 'name',
    render: ({ name }) => name
  },
  {
    label: 'Email',
    orderBy: 'email',
    render: ({ email }) => email
  },
  {
    label: 'Role',
    orderBy: 'role',
    render: ({ role }) => ROLENAMES[role]
  },
  {
    label: 'Active',
    orderBy: 'active',
    render: ({ active }) => active ? 'Yes' : 'No'
  },
  {
    label: 'Actions',
    render: ({ onRemove, ...rest }) => (
      <Flex columnGap={2}>
        <Button as={NavLink} to={`/system/users/edit/${rest.id}`} size="xs" colorScheme="blue">Edit</Button>
        <Button size="xs" colorScheme="red" onClick={() => onRemove(rest)}>Remove</Button>
      </Flex>
    )
  }
]

export const UserList = () => {
  const navigate = useNavigate()
  const disclousre = useDisclosure()
  const { createToast } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10,
    search: '',
    orderBy: {
      key: 'name',
      direction: 'asc'
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [rows, setRows] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getUsers(
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction
      )
      setRows(response.rows)
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const onRemove = (user) => {
    setSelectedUser(user)
    disclousre.onOpen()
  }

  const handleRemove = async (userId) => {
    setIsRemoving(true)
    try {
      await deleteUser(userId)
      setIsRemoving(false)
      disclousre.onClose()
      load()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsRemoving(false)
    }
  }

  const customizedRows = useMemo(() => {
    return rows.map(v => ({ ...v, onRemove }))
  }, [rows])

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">User Management</Heading>
          <Button
            size="md"
            colorScheme="blue"
            onClick={() => navigate('/system/users/add')}
            leftIcon={<MdAdd size="24px" color="white" />}
          >
            Add User
          </Button>
        </Flex>
        <UiDataTable
          tableConfig={tableConfig}
          updateTableConfig={setTableConfig}
          rows={customizedRows}
          rowKey="id"
          columns={USER_COLUMNS}
          isLoading={isLoading}
        />
      </Box>
      <ModalRemoveUser
        user={selectedUser}
        isRemoving={isRemoving}
        handleRemove={() => handleRemove(selectedUser?.id)}
        {...disclousre}
      />
    </>
  )
}

const ModalRemoveUser = ({ user, isRemoving, handleRemove, isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Remove User</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>Are you sure to remove the user - {user?.firstname} {user?.lastname}?</Text>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='red' mr={3} onClick={handleRemove} isLoading={isRemoving}>
            Remove
          </Button>
          <Button variant='ghost' onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalRemoveUser.propTypes = {
  user: PropTypes.object,
  isRemoving: PropTypes.bool,
  handleRemove: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}
